import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach token to every request
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ─── Dashboard ────────────────────────────────────────────
export const dashboardAPI = {
  getOverview: () => adminApi.get("/admin/dashboard"),
};

// ─── Videos ───────────────────────────────────────────────
export interface VideoFilters {
  page?: number;
  limit?: number;
  status?: string;
  visibility?: string;
  level?: "none" | "pre-recorded" | "beginner" | "advanced";
  isFree?: boolean;
  search?: string;
}

export type AdvancedVideoCategory =
  | "Total Face Flow"
  | "Lower Face Flow"
  | "Middle Face Flow"
  | "Upper Face Flow"
  | "Facial Massage"
  | "Gua Sha Ritual"
  | "Taping Technique"
  | "Beyond the Face";

export type PreRecordedVideoCategory =
  | "Activation"
  | "Flow 1"
  | "Flow 2"
  | "Flow 3"
  | "Flow 4"
  | "Flow 5"
  | "Release";

export type VideoCategory = AdvancedVideoCategory | PreRecordedVideoCategory;

export interface CreateVideoPayload {
  title: string;
  description?: string;
  sourceUrl?: string;
  visibility?: string;
  level?: "none" | "pre-recorded" | "beginner" | "advanced";
  plan?: "free" | "basic" | "premium";
  isFree?: boolean;
  category?: VideoCategory;
  tags?: string[];
}

export interface UpdateVideoPayload {
  title?: string;
  description?: string;
  visibility?: string;
  level?: "none" | "pre-recorded" | "beginner" | "advanced";
  plan?: "free" | "basic" | "premium";
  isFree?: boolean;
  category?: VideoCategory;
  tags?: string[];
  status?: string;
  order?: number;
}

/**
 * Largest video the API will accept, in MB.
 *
 * Keep in step with MAX_VIDEO_UPLOAD_MB on the backend and with
 * client_max_body_size for /api/admin/videos/upload in deploy/nginx.conf.
 * Checking here only saves the admin a long upload that ends in a rejection —
 * the limit that counts is the server's.
 */
export const MAX_VIDEO_UPLOAD_MB = Number(process.env.REACT_APP_MAX_VIDEO_UPLOAD_MB) || 512;
export const MAX_VIDEO_UPLOAD_BYTES = MAX_VIDEO_UPLOAD_MB * 1024 * 1024;

const formatMb = (bytes: number) => Math.round(bytes / (1024 * 1024)).toLocaleString();

/**
 * Turn an upload failure into something an admin can act on.
 *
 * A body rejected by nginx comes back as its own HTML error page, so there is
 * no `data.message` to show — without this the UI reports the bare axios text
 * ("Request failed with status code 413").
 */
const describeUploadError = (err: any): Error => {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;

  if (status === 413) {
    return new Error(
      serverMessage ||
        `The server rejected this video as too large (limit ${MAX_VIDEO_UPLOAD_MB} MB).`
    );
  }
  if (status === 507) {
    return new Error(serverMessage || "The server is out of storage for new videos.");
  }
  if (!err?.response) {
    return new Error("The upload was interrupted. Check the connection and try again.");
  }
  return new Error(serverMessage || err?.message || "Video upload failed.");
};

/**
 * Read a video file's duration in the browser, in seconds.
 *
 * Resolves to 0 when the browser cannot decode the container — the upload
 * should not fail just because we could not read a duration.
 */
const readVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const probe = document.createElement("video");
    const done = (seconds: number) => {
      URL.revokeObjectURL(objectUrl);
      resolve(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0);
    };
    probe.preload = "metadata";
    probe.onloadedmetadata = () => done(probe.duration);
    probe.onerror = () => done(0);
    probe.src = objectUrl;
  });

export const videosAPI = {
  list: (filters?: VideoFilters) =>
    adminApi.get("/admin/videos", { params: filters }),
  get: (id: string) => adminApi.get(`/admin/videos/${id}`),
  create: (data: CreateVideoPayload) => adminApi.post("/admin/videos", data),
  update: (id: string, data: UpdateVideoPayload) =>
    adminApi.put(`/admin/videos/${id}`, data),
  delete: (id: string) => adminApi.delete(`/admin/videos/${id}`),
  updateVisibility: (id: string, visibility: string) =>
    adminApi.patch(`/admin/videos/${id}/visibility`, { visibility }),
  getDirectUploadUrl: (title: string) =>
    adminApi.post("/admin/videos/direct-upload", { title }),
  confirmUpload: (videoId: string) =>
    adminApi.post(`/admin/videos/${videoId}/confirm-upload`),
  /**
   * Upload a video file through our own API.
   *
   * The server decides where the bytes land: Cloudflare Stream when it is
   * configured, otherwise this server's own /uploads/videos directory. Either
   * way the browser talks only to our API, so there is no upload path that
   * silently does nothing when Stream is switched off.
   */
  uploadFile: async (
    file: File,
    title: string,
    onProgress?: (progress: number) => void
  ): Promise<{ videoId: string; cloudflareVideoId: string }> => {
    // Fail here rather than after uploading hundreds of megabytes only to be
    // turned away by the server.
    if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
      throw new Error(
        `This video is ${formatMb(file.size)} MB — the limit is ${MAX_VIDEO_UPLOAD_MB} MB. ` +
          `Compress it and try again.`
      );
    }

    // Only the browser can read the duration off the file — a self-hosted
    // video has no encoder behind it to report one.
    const duration = await readVideoDuration(file);

    const form = new FormData();
    form.append("video", file);
    form.append("title", title);
    form.append("duration", String(duration));

    let response;
    try {
      response = await adminApi.post("/admin/videos/upload", form, {
        // Required: this instance defaults to application/json, and axios
        // serialises FormData to JSON when the content type says JSON — the file
        // would never leave the browser. The browser fills in the boundary.
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0, // large files; never time the upload out
        onUploadProgress: (event) => {
          if (!onProgress) return;
          const total = event.total || file.size;
          if (total) onProgress(Math.round((event.loaded / total) * 100));
        },
      });
    } catch (err: any) {
      throw describeUploadError(err);
    }

    const video = response.data;
    return { videoId: video._id, cloudflareVideoId: video.cloudflareVideoId };
  },
};

// ─── Products ─────────────────────────────────────────────
export interface ProductFilters {
  page?: number;
  limit?: number;
  status?: string;
  type?: string;
  search?: string;
}

export interface CreateProductPayload {
  name: string;
  description?: string;
  type: string;
  price: number;
  currency?: string;
  status?: string;
  thumbnail?: string;
  image?: string;
  images?: string[];
  videos?: string[];
  features?: string[];
  // Physical product fields
  sku?: string;
  stock?: number;
  weight?: number;
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
}

export interface UpdateProductPayload {
  name?: string;
  description?: string;
  type?: string;
  price?: number;
  currency?: string;
  status?: string;
  thumbnail?: string;
  image?: string;
  images?: string[];
  videos?: string[];
  features?: string[];
  // Physical product fields
  sku?: string;
  stock?: number;
  weight?: number;
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
}

export const productsAPI = {
  list: (filters?: ProductFilters) =>
    adminApi.get("/admin/products", { params: filters }),
  get: (id: string) => adminApi.get(`/admin/products/${id}`),
  create: (data: CreateProductPayload | FormData) => {
    const isFormData = data instanceof FormData;
    return adminApi.post("/admin/products", data, {
      headers: isFormData ? { "Content-Type": "multipart/form-data" } : {},
    });
  },
  update: (id: string, data: UpdateProductPayload | FormData) => {
    const isFormData = data instanceof FormData;
    return adminApi.put(`/admin/products/${id}`, data, {
      headers: isFormData ? { "Content-Type": "multipart/form-data" } : {},
    });
  },
  delete: (id: string) => adminApi.delete(`/admin/products/${id}`),
  assignVideos: (id: string, videoIds: string[]) =>
    adminApi.put(`/admin/products/${id}/videos`, { videoIds }),
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append("image", file);
    return adminApi.post("/admin/products/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// ─── Orders ───────────────────────────────────────────────
export interface OrderFilters {
  page?: number;
  limit?: number;
  status?: string;
  paymentStatus?: string;
  search?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateOrderPayload {
  status?: string;
  paymentStatus?: string;
  paymentId?: string;
  notes?: string;
}

export const ordersAPI = {
  list: (filters?: OrderFilters) =>
    adminApi.get("/admin/orders", { params: filters }),
  get: (id: string) => adminApi.get(`/admin/orders/${id}`),
  getByOrderNumber: (orderNumber: string) =>
    adminApi.get(`/admin/orders/by-number/${orderNumber}`),
  getStats: () => adminApi.get("/admin/orders/stats"),
  update: (id: string, data: UpdateOrderPayload) =>
    adminApi.put(`/admin/orders/${id}`, data),
  updateStatus: (id: string, status: string) =>
    adminApi.patch(`/admin/orders/${id}/status`, { status }),
  updatePaymentStatus: (id: string, paymentStatus: string, paymentId?: string) =>
    adminApi.patch(`/admin/orders/${id}/payment-status`, { paymentStatus, paymentId }),
  delete: (id: string) => adminApi.delete(`/admin/orders/${id}`),
};

// ─── Content ──────────────────────────────────────────────
export interface ContentFilters {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
  search?: string;
}

export interface CreateContentPayload {
  title: string;
  slug?: string;
  type: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  metadata?: {
    seoTitle?: string;
    seoDescription?: string;
    keywords?: string[];
  };
  status?: string;
  order?: number;
  startDate?: string;
  endDate?: string;
}

export interface UpdateContentPayload {
  title?: string;
  slug?: string;
  type?: string;
  content?: string;
  excerpt?: string;
  featuredImage?: string;
  metadata?: {
    seoTitle?: string;
    seoDescription?: string;
    keywords?: string[];
  };
  status?: string;
  order?: number;
  startDate?: string;
  endDate?: string;
}

export const contentAPI = {
  list: (filters?: ContentFilters) =>
    adminApi.get("/admin/content", { params: filters }),
  get: (id: string) => adminApi.get(`/admin/content/${id}`),
  getStats: () => adminApi.get("/admin/content/stats"),
  create: (data: CreateContentPayload) =>
    adminApi.post("/admin/content", data),
  update: (id: string, data: UpdateContentPayload) =>
    adminApi.put(`/admin/content/${id}`, data),
  updateStatus: (id: string, status: string) =>
    adminApi.patch(`/admin/content/${id}/status`, { status }),
  updateOrder: (id: string, order: number) =>
    adminApi.patch(`/admin/content/${id}/order`, { order }),
  duplicate: (id: string) =>
    adminApi.post(`/admin/content/${id}/duplicate`),
  delete: (id: string) => adminApi.delete(`/admin/content/${id}`),
};

// ─── Auth ─────────────────────────────────────────────────
export const authAPI = {
  login: (email: string, password: string) =>
    adminApi.post("/auth/login", { email, password }),
  me: () => adminApi.get("/auth/me"),
};

// ─── Courses ──────────────────────────────────────────────
export interface CourseFilters {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

// ─── Survey / Quiz ────────────────────────────────────────
export interface SurveyQuestion {
  id: string;
  text: string;
  type: "radio" | "checkbox" | "text" | "dropdown";
  options: string[];
  correctAnswer?: string | string[];
  required: boolean;
}

export interface SurveyConfig {
  mode: "survey" | "quiz";
  description?: string;
  passingScore?: number;
  questions: SurveyQuestion[];
}

export interface SurveySubmissionAnswer {
  questionId: string;
  questionText: string;
  questionType: string;
  answer: string | string[];
}

export interface SurveySubmission {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  courseId: string;
  courseTitle: string;
  phaseTitle: string;
  itemId: string;
  itemTitle: string;
  mode: "survey" | "quiz";
  answers: SurveySubmissionAnswer[];
  score?: number;
  passed?: boolean;
  submittedAt: string;
}

export const surveySubmissionsAPI = {
  list: (params?: {
    courseId?: string;
    page?: number;
    limit?: number;
    search?: string;
    mode?: "survey" | "quiz";
  }) => adminApi.get("/admin/survey-submissions", { params }),
  get: (id: string) => adminApi.get(`/admin/survey-submissions/${id}`),
  delete: (id: string) => adminApi.delete(`/admin/survey-submissions/${id}`),
};

export interface PhaseItemPayload {
  type: "video" | "pdf" | "letter" | "survey";
  title: string;
  titleAr?: string;
  video?: string;
  pdfUrl?: string;
  pdfUrlAr?: string;
  pdfPages?: number;
  letterContent?: string;
  letterContentAr?: string;
  surveyConfig?: SurveyConfig;
  order: number;
}

export interface CoursePhasePayload {
  title: string;
  titleAr?: string;
  order: number;
  items: PhaseItemPayload[];
}

export interface CreateCoursePayload {
  title: string;
  titleAr?: string;
  description?: string;
  descriptionAr?: string;
  thumbnail?: string;
  status?: string;
  sessionType?: "group" | "private" | null;
  assignedUserId?: string | null;
  zoomMeetingId?: string | null;
  phases?: CoursePhasePayload[];
}

export interface UpdateCoursePayload {
  title?: string;
  titleAr?: string;
  description?: string;
  descriptionAr?: string;
  thumbnail?: string;
  status?: string;
  sessionType?: "group" | "private" | null;
  assignedUserId?: string | null;
  zoomMeetingId?: string | null;
  phases?: CoursePhasePayload[];
}

export interface CourseUserProgressRow {
  userId: string;
  name: string;
  email: string;
  completedCount: number;
  totalItems: number;
  progressPercentage: number;
  commentCount: number;
  likeCount: number;
  likedCourse: boolean;
  isLocked: boolean;
  lockReason?: string;
  lastActivityAt?: string | null;
  learningStatus: "not_started" | "in_progress" | "completed" | "halted";
  currentPosition: string;
}

export interface CourseUserProgressSummary {
  totalUsers: number;
  startedUsers: number;
  inProgressUsers: number;
  completedUsers: number;
  haltedUsers: number;
  lockedUsers: number;
  totalComments: number;
  totalLikes: number;
  courseLikes: number;
  averageProgress: number;
}

export const coursesAPI = {
  list: (filters?: CourseFilters) =>
    adminApi.get("/admin/courses", { params: filters }),
  get: (id: string) => adminApi.get(`/admin/courses/${id}`),
  create: (data: CreateCoursePayload) =>
    adminApi.post("/admin/courses", data),
  update: (id: string, data: UpdateCoursePayload) =>
    adminApi.put(`/admin/courses/${id}`, data),
  delete: (id: string) => adminApi.delete(`/admin/courses/${id}`),
  stats: () => adminApi.get("/admin/courses/stats"),
  uploadPdf: (file: File) => {
    const formData = new FormData();
    formData.append("pdf", file);
    return adminApi.post("/admin/courses/upload-pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  /**
   * Stream a PDF from R2 through the backend and return as an ArrayBuffer.
   * Use this instead of getPdfSignedUrl to avoid CORS/access issues with direct R2 URLs.
   */
  streamPdf: (pdfKey: string) =>
    adminApi.get<ArrayBuffer>("/admin/courses/pdf-stream", {
      params: { key: pdfKey },
      responseType: "arraybuffer",
    }),

  /**
   * Fetch a short-lived signed R2 URL for viewing or downloading a PDF.
   * Returns the direct R2 URL so the iframe can load it without a proxy redirect.
   */
  getPdfSignedUrl: (pdfKey: string, mode: "view" | "download" = "view") =>
    adminApi.get<{ url: string }>("/admin/courses/pdf-signed-url", {
      params: { key: pdfKey, mode },
    }),

  /** @deprecated use getPdfSignedUrl — kept for any legacy callers */
  getPdfProxyUrl: (rawPdfUrl: string, mode: "view" | "download" = "view"): string => {
    if (!rawPdfUrl) return "";
    const base = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    return `${base}/admin/courses/pdf-proxy?url=${encodeURIComponent(rawPdfUrl)}&mode=${mode}`;
  },
  uploadThumbnail: (file: File) => {
    const formData = new FormData();
    formData.append("image", file);
    return adminApi.post("/admin/courses/upload-thumbnail", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getUserProgress: (id: string, params?: { search?: string; page?: number; limit?: number }) =>
    adminApi.get(`/courses/admin/${id}/user-progress`, { params }),
  toggleUserLock: (
    id: string,
    userId: string,
    payload: { isLocked: boolean; lockReason?: string }
  ) => adminApi.patch(`/courses/admin/${id}/user-progress/${userId}/lock`, payload),
};

// ─── Banners ───────────────────────────────────────────────────────────────────
export interface Banner {
  _id: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  cfImageId?: string;
  linkUrl?: string;
  linkLabel?: string;
  type: "image" | "text";
  bgColor?: string;
  textColor?: string;
  order: number;
  active: boolean;
  page: "classes" | "shop";
  createdAt: string;
  updatedAt: string;
}

export interface BannerPayload {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  cfImageId?: string;
  linkUrl?: string;
  linkLabel?: string;
  type: "image" | "text";
  bgColor?: string;
  textColor?: string;
  order?: number;
  active?: boolean;
  page?: "classes" | "shop";
}

export const bannersAPI = {
  list: (page?: "classes" | "shop") =>
    adminApi.get<{ banners: Banner[] }>("/admin/banners", { params: page ? { page } : undefined }),
  create: (payload: BannerPayload) => adminApi.post<{ banner: Banner }>("/admin/banners", payload),
  update: (id: string, payload: Partial<BannerPayload>) =>
    adminApi.put<{ banner: Banner }>(`/admin/banners/${id}`, payload),
  remove: (id: string) => adminApi.delete(`/admin/banners/${id}`),
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append("image", file);
    return adminApi.post<{ url: string; cfImageId: string }>(
      "/admin/banners/upload-image",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },
};

export default adminApi;

// ─── Users (Admin) ────────────────────────────────────────
export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: "user" | "admin";
  isActive: boolean;
  step1Completed: boolean;
  avatar?: string;
  phone?: string;
  createdAt: string;
  lastLoginAt?: string;
  subscription?: {
    plan: string;
    billingCycle: string;
    endDate: string;
    status: string;
  } | null;
}

export interface AdminUserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

export const usersAdminAPI = {
  list: (filters?: AdminUserFilters) =>
    adminApi.get<{ success: boolean; data: AdminUser[]; pagination: any }>(
      "/users/admin/list",
      { params: filters }
    ),
  get: (id: string) =>
    adminApi.get<{ success: boolean; data: AdminUser }>(`/users/admin/list/${id}`),
  update: (id: string, data: { role?: string; isActive?: boolean; step1Completed?: boolean; name?: string; email?: string; phone?: string }) =>
    adminApi.patch<{ success: boolean; data: AdminUser }>(`/users/admin/list/${id}`, data),
  delete: (id: string) =>
    adminApi.delete<{ success: boolean; message: string }>(`/users/admin/list/${id}`),
  create: (data: { name: string; email: string; password: string; role?: string; phone?: string }) =>
    adminApi.post<{ success: boolean; data: AdminUser }>("/users/admin/list", data),
};

// ─── Referrals ──────────────────────────────────────────────────────────────
export interface ReferralRecord {
  _id: string;
  referrerName: string;
  referrerEmail: string;
  referredName: string;
  referredEmail: string;
  createdAt: string;
}

export interface ReferralFilters {
  page?: number;
  limit?: number;
}

export const referralsAPI = {
  list: (filters?: ReferralFilters) =>
    adminApi.get<{ success: boolean; data: ReferralRecord[]; pagination: any }>(
      "/admin/referrals",
      { params: filters }
    ),
};

// ─── Coupons ────────────────────────────────────────────────────────────────
export interface Coupon {
  _id: string;
  code: string;
  type: "percent" | "flat";
  value: number;
  active: boolean;
  expiresAt?: string;
  maxUses?: number;
  usedCount: number;
  applicablePlans?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CouponPayload {
  code: string;
  type: "percent" | "flat";
  value: number;
  active?: boolean;
  expiresAt?: string;
  maxUses?: number;
  applicablePlans?: string[];
}

export const couponsAPI = {
  list: () => adminApi.get<{ coupons: Coupon[] }>("/admin/coupons"),
  create: (payload: CouponPayload) => adminApi.post<{ coupon: Coupon }>("/admin/coupons", payload),
  update: (id: string, payload: Partial<CouponPayload>) =>
    adminApi.put<{ coupon: Coupon }>(`/admin/coupons/${id}`, payload),
  remove: (id: string) => adminApi.delete(`/admin/coupons/${id}`),
};

// ─── Bookings ───────────────────────────────────────────────────────────────────
export interface BookingFilters {
  page?: number;
  limit?: number;
  status?: "pending" | "accepted" | "rejected" | "completed" | "cancelled";
  sessionType?: "private" | "group";
  search?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export const bookingsAPI = {
  getBookings: (filters?: BookingFilters) =>
    adminApi.get("/admin/bookings", { params: filters }),
  getBooking: (id: string) =>
    adminApi.get(`/admin/bookings/${id}`),
  updateBooking: (id: string, data: { status?: string; assignedDate?: string; assignedTime?: string; adminNotes?: string }) =>
    adminApi.put(`/admin/bookings/${id}`, data),
  deleteBooking: (id: string) =>
    adminApi.delete(`/admin/bookings/${id}`),
};

// ── Availability Schedule ─────────────────────────────────────────────────────
export interface TimeRange {
  start: string; // "09:00"
  end: string;   // "11:00"
}

export interface AvailabilitySchedule {
  _id?: string;
  dayOfWeek: number;
  sessionType: "group" | "private" | "both";
  timeRanges: TimeRange[];
  isActive: boolean;
}

export const availabilityAPI = {
  getAll: () => adminApi.get("/admin/availability"),
  upsert: (data: Omit<AvailabilitySchedule, "_id">) =>
    adminApi.post("/admin/availability", data),
  delete: (id: string) => adminApi.delete(`/admin/availability/${id}`),
  // Public (no auth needed)
  getPublic: (sessionType: "group" | "private") =>
    adminApi.get(`/availability/public/${sessionType}`),
  getSlotsForDate: (date: string, sessionType: "group" | "private") =>
    adminApi.get("/availability/slots", { params: { date, sessionType } }),
};

// ── Zoom Meetings ─────────────────────────────────────────────────────────────

export interface ZoomMeetingSettings {
  hostVideo?: boolean;
  participantVideo?: boolean;
  joinBeforeHost?: boolean;
  muteUponEntry?: boolean;
  waitingRoom?: boolean;
  approvalType?: number;
  audio?: "both" | "telephony" | "voip";
  autoRecording?: "local" | "cloud" | "none";
}

export interface ZoomMeetingFilters {
  page?: number;
  limit?: number;
  status?: "scheduled" | "started" | "ended" | "cancelled";
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateZoomMeetingPayload {
  topic: string;
  agenda?: string;
  meetingType?: 1 | 2 | 3 | 8;
  startTime: string; // ISO string
  duration: number;
  timezone?: string;
  password?: string;
  settings?: ZoomMeetingSettings;
  bookingId?: string;
  allowedRoles?: ("admin" | "member" | "all")[];
  isPublic?: boolean;
  maxParticipants?: number;
}

export interface UpdateZoomMeetingPayload {
  topic?: string;
  agenda?: string;
  startTime?: string;
  duration?: number;
  timezone?: string;
  password?: string;
  status?: "scheduled" | "started" | "ended" | "cancelled";
  settings?: ZoomMeetingSettings;
  allowedRoles?: ("admin" | "member" | "all")[];
  isPublic?: boolean;
  maxParticipants?: number;
}

export const zoomMeetingsAPI = {
  list: (filters?: ZoomMeetingFilters) =>
    adminApi.get("/admin/zoom-meetings", { params: filters }),
  getOne: (id: string) =>
    adminApi.get(`/admin/zoom-meetings/${id}`),
  create: (data: CreateZoomMeetingPayload) =>
    adminApi.post("/admin/zoom-meetings", data),
  update: (id: string, data: UpdateZoomMeetingPayload) =>
    adminApi.put(`/admin/zoom-meetings/${id}`, data),
  remove: (id: string) =>
    adminApi.delete(`/admin/zoom-meetings/${id}`),
  sync: (id: string) =>
    adminApi.post(`/admin/zoom-meetings/${id}/sync`),
  participants: (id: string) =>
    adminApi.get(`/admin/zoom-meetings/${id}/participants`),
};

// ─── Contact Messages ─────────────────────────────────────────────────────────
export interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: "unread" | "read" | "replied";
  replyText?: string;
  repliedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const contactAdminAPI = {
  list: (params?: { page?: number; limit?: number; status?: string }) =>
    adminApi.get<{ success: boolean; data: ContactMessage[]; pagination: any }>(
      "/contact/admin",
      { params }
    ),
  markRead: (id: string) =>
    adminApi.patch<{ success: boolean; data: ContactMessage }>(`/contact/admin/${id}/read`),
  reply: (id: string, replyText: string) =>
    adminApi.post<{ success: boolean; data: ContactMessage }>(`/contact/admin/${id}/reply`, { replyText }),
  delete: (id: string) =>
    adminApi.delete<{ success: boolean }>(`/contact/admin/${id}`),
};

// ─── Admin Notifications ──────────────────────────────────────────────────────
export type AdminNotificationType =
  | "user.registered"
  | "subscription.requested"
  | "subscription.activated";

export interface AdminNotification {
  _id: string;
  type: AdminNotificationType;
  title: string;
  message: string;
  /** Admin-panel path to open, e.g. "/admin/subscriptions". */
  link?: string;
  data?: Record<string, any>;
  readAt?: string | null;
  createdAt: string;
}

export const notificationsAPI = {
  list: (params?: { page?: number; limit?: number; unread?: boolean }) =>
    adminApi.get<{
      success: boolean;
      data: AdminNotification[];
      unreadCount: number;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>("/admin/notifications", { params }),
  unreadCount: () =>
    adminApi.get<{ success: boolean; unreadCount: number }>("/admin/notifications/unread-count"),
  markRead: (id: string) =>
    adminApi.patch<{ success: boolean; data: AdminNotification }>(`/admin/notifications/${id}/read`),
  markAllRead: () =>
    adminApi.patch<{ success: boolean; updated: number }>("/admin/notifications/read-all"),
};

// ─── Email Blast ──────────────────────────────────────────────────────────────
export interface EmailUser {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
  isActive: boolean;
}

export const adminEmailAPI = {
  listUsers: (params?: { search?: string; page?: number; limit?: number }) =>
    adminApi.get<{
      success: boolean;
      data: EmailUser[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>("/admin/emails/users", { params }),

  send: (data: { recipients: { email: string; name: string }[]; subject: string; body: string }) =>
    adminApi.post<{ success: boolean; sent: number; failed: number }>("/admin/emails/send", data),
};

// ─── Home Before & After Section ──────────────────────────────────────────────
export interface HomeBaRow {
  _id?: string;
  beforeImageUrl: string;
  afterImageUrl:  string;
  tag:            string;
  weeks?:         number;
  description?:   string;
  cloudflareVideoId: string;
  videoTitle?:    string;
  reverse?:       boolean;
  order:          number;
}

export const homeBaAPI = {
  list:   ()                         => adminApi.get<{ data: HomeBaRow[] }>("/admin/home-ba"),
  create: (data: Omit<HomeBaRow,"_id">) => adminApi.post<{ data: HomeBaRow }>("/admin/home-ba", data),
  update: (id: string, data: Partial<HomeBaRow>) => adminApi.put<{ data: HomeBaRow }>(`/admin/home-ba/${id}`, data),
  remove: (id: string)               => adminApi.delete(`/admin/home-ba/${id}`),
  /* Public fetch — used by the home page (still passes token if present) */
  listPublic: ()                     => adminApi.get<{ data: HomeBaRow[] }>("/home-ba"),
};

